import { useEffect, useMemo, useState } from "react";
import {
  MiChip,
  MiDetailPageHeader,
  MiModalConfirm,
  MiTab,
} from "@miplatform/components";
import { MODAL_TITLES, VENDOR_ACCOUNT_TYPES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiChevronLeft } from "@mdi/js";
import { Box } from "@mui/material";
import { AccountContactsTab } from "./AccountContactsTab/AccountContactsTab";
import { AccountHomesTab } from "./AccountHomesTab/AccountHomesTab";
import { AccountSummaryTab } from "./AccountSummaryTab/AccountSummaryTab";
import { AccountUsersTab } from "./AccountUsersTab/AccountUsersTab";
import { ACCOUNT_DETAILS_TABS_VALUE } from "./constants";
import { useAccountDetailsViewModel } from "./useAccountDetailsViewModel";

export function AccountDetails() {
  const { params, navigate, location } = useRouter();
  const { id: accountId, tab, accountTab } = params;
  const { state } = location;
  const readOnly = state?.readOnly ?? true;

  const [editMode, setEditMode] = useState(!readOnly);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const {
    isLoading,
    stateManager,
    accountDetail,
    setAccountDetail,
    getAccountDetails,
  } = useAccountDetailsViewModel();
  const pagePath = useMemo(
    () =>
      VENDOR_ACCOUNT_TYPES.includes(accountDetail?.accountType)
        ? "vendors"
        : "customers",
    [accountDetail?.accountType]
  );

  useEffect(() => {
    getAccountDetails(accountId);
  }, []);

  const handleTabChange = (accountTab) => {
    setEditMode(false);
    if (isLoading) {
      return;
    }
    navigate(`/accounting/${pagePath}/${tab}/${accountId}/${accountTab}`);
  };

  const getTabs = () => {
    const baseTabs = [
      { label: "Summary", value: ACCOUNT_DETAILS_TABS_VALUE.SUMMARY },
      { label: "Contacts", value: ACCOUNT_DETAILS_TABS_VALUE.CONTACTS },
    ];

    if (VENDOR_ACCOUNT_TYPES.includes(accountDetail?.accountType)) {
      const vendorTabs = [
        { label: "Users", value: ACCOUNT_DETAILS_TABS_VALUE.USERS },
      ];

      return [...baseTabs, ...vendorTabs];
    }

    baseTabs.splice(1, 0, {
      label: "Homes",
      value: ACCOUNT_DETAILS_TABS_VALUE.HOMES,
    });

    return baseTabs;
  };

  return (
    <>
      <MiDetailPageHeader
        title={
          <Box display="flex" alignItems="center" gap={2}>
            {accountDetail.status && (
              <MiChip
                color={accountDetail.status === "Active" ? "success" : "error"}
                label={accountDetail.status}
                sx={{ borderRadius: 1 }}
              />
            )}
            {accountDetail.name}
          </Box>
        }
        subTitles={[accountDetail.accountType]}
        titleIcon={mdiChevronLeft}
        loading={isLoading}
        customOnBack={editMode ? () => setOpenCancelModal(true) : undefined}
      />
      {accountDetail?.accountType && (
        <MiTab
          tabs={getTabs()}
          currentTab={accountTab}
          onTabChange={handleTabChange}
        />
      )}
      {accountTab === ACCOUNT_DETAILS_TABS_VALUE.SUMMARY && (
        <AccountSummaryTab
          tab={tab}
          stateManager={stateManager}
          accountDetail={accountDetail}
          setAccountDetail={setAccountDetail}
          getAccountDetails={getAccountDetails}
        />
      )}
      {accountTab === ACCOUNT_DETAILS_TABS_VALUE.HOMES && (
        <AccountHomesTab
          stateManager={stateManager}
          accountId={accountId}
          account={accountDetail}
        />
      )}
      {accountTab === ACCOUNT_DETAILS_TABS_VALUE.CONTACTS && (
        <AccountContactsTab
          stateManager={stateManager}
          accountDetail={accountDetail}
        />
      )}
      {accountTab === ACCOUNT_DETAILS_TABS_VALUE.USERS && (
        <AccountUsersTab accountDetail={accountDetail} />
      )}
      <MiModalConfirm
        title={MODAL_TITLES.CANCEL}
        open={openCancelModal}
        handleNegative={() => setOpenCancelModal(false)}
        handlePositive={() => navigate(`/accounting/${pagePath}/${tab}`)}
        description="Are you sure you want to cancel editing this account?"
      />
    </>
  );
}
